import type { SignupSubRouteRouteContext } from 'router/utils';

import { readLeadQueryCache } from 'services/partnersSelfSignup/signup';
import { getFileQueryOptions } from 'services/partnersSelfSignup/upload-file';

/**
 * Shared loader function for loading W9 tax files across multiple routes.
 *
 * This function checks if the current lead has a W9 file and preloads it using
 * TanStack Query's `query()` for optimal caching and deferred loading.
 *
 * @param context - The signup sub-route context containing queryClient, queryVariables, and flags
 * @returns Promise resolving to the W9 file data or undefined if no file exists
 *
 * @see https://tanstack.com/router/v1/docs/framework/react/guide/deferred-data-loading#deferred-data-loading-with-external-libraries
 */
export async function loadW9File({
  queryClient,
  queryVariables,
}: SignupSubRouteRouteContext) {
  const result = readLeadQueryCache(queryClient, queryVariables);
  const { lead } = result ?? {};

  const fileId = lead?.w9?.file?.id ?? '';
  if (!fileId) return undefined;

  return queryClient.query(
    getFileQueryOptions({
      fileId,
      businessEmail: queryVariables.businessEmail,
    }),
  );
}
